import React, { useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useTheme } from '../../contexts/ThemeContext';
import analyticsService from '../../services/analyticsService';
import { LAYOUT_CONFIGS, getRequiredChartCount } from '../../utils/chartLayoutConfig';
import { headerLabeledIconButtonStyle } from '../../utils/headerIconButtonStyle';

const ChartLayoutSelector = ({
  currentLayout,
  onLayoutChange,
  showLabel = false,
  compact = false,
  bare = false,
  variant = 'default',
}) => {
  const { colors, isLight } = useTheme();
  const [show, setShow] = useState(false);
  const isHeaderIcon = variant === 'headerIcon';

  const layouts = LAYOUT_CONFIGS.map(({ value, label, icon }) => ({ value, label, icon }));

  const currentLayoutData = layouts.find(l => l.value === currentLayout) || layouts.find(l => l.value === 'side-by-side') || layouts[0];
  const chartCount = getRequiredChartCount(currentLayout);

  const handleSelect = (layoutValue) => {
    analyticsService.trackTradingEvent('chart_layout_changed', {
      layout: layoutValue
    });

    if (typeof onLayoutChange === 'function') {
      onLayoutChange(layoutValue);
    }
    setShow(false);
  };

  const toggleStyle = isHeaderIcon
    ? headerLabeledIconButtonStyle(colors, { isLight, active: show })
    : bare
    ? {
        backgroundColor: 'transparent',
        border: 'none',
        color: colors.text,
        padding: '4px 6px',
        fontSize: compact ? 13 : 14,
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 5 : 6,
        minHeight: compact ? 32 : undefined,
        boxShadow: 'none',
        cursor: 'pointer',
      }
    : {
        backgroundColor: colors.bgSecondary,
        border: `1px solid ${colors.border}`,
        color: colors.text,
        padding: '6px 12px',
        fontSize: compact ? 13 : 14,
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 5 : 6,
        borderRadius: 6,
        minHeight: compact ? 32 : undefined,
      };

  return (
    <Dropdown show={show} onToggle={setShow} align={isHeaderIcon ? 'end' : undefined}>
      <Dropdown.Toggle
        variant="secondary"
        className="header-dropdown-no-caret"
        style={toggleStyle}
        aria-label={isHeaderIcon ? `Number of charts, ${chartCount}` : 'Chart arrangement'}
        title={isHeaderIcon ? 'Number of charts' : 'Chart arrangement'}
      >
        {(compact || showLabel || bare || isHeaderIcon) ? (
          <>
            {currentLayoutData.icon}
            <span>{isHeaderIcon ? chartCount : 'Layout'}</span>
          </>
        ) : (
          currentLayoutData.icon
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu
        style={{
          backgroundColor: colors.bgSecondary,
          border: `1px solid ${colors.border}`,
          borderRadius: 8,
          padding: 4,
          minWidth: 200,
        }}
      >
        {layouts.map((layout) => (
          <Dropdown.Item
            key={layout.value}
            onClick={() => handleSelect(layout.value)}
            style={{
              backgroundColor: layout.value === currentLayout ? colors.primary + '20' : 'transparent',
              color: layout.value === currentLayout ? colors.primary : colors.text,
              padding: '8px 12px',
              borderRadius: 6,
              fontSize: 14,
              fontWeight: layout.value === currentLayout ? 600 : 400,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
            }}
            onMouseEnter={(e) => {
              if (layout.value !== currentLayout) {
                e.target.style.backgroundColor = colors.primary + '0D';
              }
            }}
            onMouseLeave={(e) => {
              if (layout.value !== currentLayout) {
                e.target.style.backgroundColor = 'transparent';
              }
            }}
          >
            {layout.icon}
            {layout.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ChartLayoutSelector;
